/**
 * Direct frame ingestion for live tracks (camera, screen, guest): VideoFrames read straight from the
 * track with MediaStreamTrackProcessor instead of a detached <video> element.
 *
 * The main-thread constructor is used because the compositor uploads textures on the main thread, so
 * a worker would only add a per-frame postMessage hop. It is a Chromium extension (the specification
 * exposes the processor to dedicated workers only), hence the feature detection and the element
 * fallback. Reading is promise-driven, not timer-driven, so hidden-tab timer throttling does not
 * apply to it.
 */

export type LiveTrackIngestion = 'element' | 'processor';

let mode: LiveTrackIngestion = 'element';
const listeners = new Set<() => void>();

/**
 * Chooses how live tracks become pictures. Running sources re-present immediately; 'processor' falls
 * back to the element per source when the browser lacks MediaStreamTrackProcessor.
 */
export function setLiveTrackIngestion(next: LiveTrackIngestion): void {
  if (next === mode) return;
  mode = next;
  for (const l of [...listeners]) l();
}

export function getLiveTrackIngestion(): LiveTrackIngestion {
  return mode;
}

export function onIngestionChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function processorSupported(): boolean {
  return typeof MediaStreamTrackProcessor === 'function';
}

export interface TrackFrameHandlers {
  /** Receives ownership of the frame: it must be close()d by the receiver. */
  onFrame(frame: VideoFrame): void;
  /** The stream ended (track ended or reader failed) without stop() being called. */
  onClose(): void;
}

export class TrackFrameReader {
  private reader: ReadableStreamDefaultReader<VideoFrame> | null = null;
  private stopped = false;

  constructor(
    private readonly track: MediaStreamTrack,
    private readonly handlers: TrackFrameHandlers,
  ) {}

  /** Throws if the processor cannot be created for this track. */
  start(): void {
    // A small queue lets the browser drop (and close) old frames if the page falls behind: only the latest matters.
    const processor = new MediaStreamTrackProcessor({ track: this.track as MediaStreamVideoTrack, maxBufferSize: 2 });
    this.reader = processor.readable.getReader();
    void this.pump(this.reader);
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.reader?.cancel().catch(() => {});
  }

  private async pump(reader: ReadableStreamDefaultReader<VideoFrame>): Promise<void> {
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        if (this.stopped) {
          value.close();
          break;
        }
        this.handlers.onFrame(value);
      }
    } catch {
      // Cancelled by stop() or the track failed; both end the loop.
    } finally {
      if (!this.stopped) this.handlers.onClose();
    }
  }
}
